import { useState, useEffect } from 'react';
import { Modal, Stack, TextInput, Textarea, Group, Button, Divider } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { cohortsApi } from '../../../services/api/cohorts';
import type { CohortWithDetails } from '../../../types';
import { TimeSlotSection } from './TimeSlotSection';
import { parseDateOnly, toISODateString, toDateValue } from '../../../utils/dateHelpers';

interface EditCohortModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onUpdate: (cohort: CohortWithDetails) => void;
}

export function EditCohortModal({
  opened,
  onClose,
  cohort,
  onUpdate,
}: EditCohortModalProps) {
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: {
      name: cohort.name,
      code: cohort.code || '',
      description: cohort.description || '',
      startDate: parseDateOnly(cohort.start_date),
      endDate: parseDateOnly(cohort.end_date),
    },
  });

  // Reset form when modal opens with new cohort data
  useEffect(() => {
    if (opened) {
      form.setValues({
        name: cohort.name,
        code: cohort.code || '',
        description: cohort.description || '',
        startDate: parseDateOnly(cohort.start_date),
        endDate: parseDateOnly(cohort.end_date),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, cohort]);

  const handleSave = async () => {
    const values = form.values;

    // Validate
    if (!values.name.trim()) {
      form.setFieldError('name', 'Name is required');
      return;
    }
    if (!values.code.trim()) {
      form.setFieldError('code', 'Code is required');
      return;
    }

    try {
      setSaving(true);
      const updateData = {
        name: values.name,
        code: values.code,
        description: values.description || undefined,
        start_date: toISODateString(values.startDate),
        end_date: toISODateString(values.endDate),
      };

      const updated = await cohortsApi.update(cohort.id, updateData);
      onUpdate(updated);
      onClose();
      notifications.show({
        title: 'Success',
        message: 'Cohort updated successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update cohort',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Cohort"
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label="Cohort Name"
          placeholder="e.g., Spring 2024 Leadership Cohort"
          required
          {...form.getInputProps('name')}
        />

        <TextInput
          label="Cohort Code"
          placeholder="e.g., ACME-SP24-LC"
          required
          {...form.getInputProps('code')}
        />

        <Group grow>
          <DateInput
            label="Start Date"
            placeholder="Select start date"
            valueFormat="MM/DD/YYYY"
            clearable
            value={form.values.startDate}
            onChange={(value) => form.setFieldValue('startDate', toDateValue(value))}
          />
          <DateInput
            label="End Date"
            placeholder="Select end date"
            valueFormat="MM/DD/YYYY"
            clearable
            value={form.values.endDate}
            onChange={(value) => form.setFieldValue('endDate', toDateValue(value))}
            minDate={form.values.startDate || undefined}
          />
        </Group>

        <Textarea
          label="Description"
          placeholder="Brief description of this cohort..."
          minRows={3}
          {...form.getInputProps('description')}
        />

        <Divider my="md" />

        <TimeSlotSection cohortId={cohort.id} cohortCode={cohort.code} />

        <Group justify="flex-end" className="modal-footer">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
